import React, { useEffect, useState } from 'react'
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/ui'
import type { ApiError } from '@/shared/api'
import { useOrderDetails, useOrderHistory } from '@/shared/hooks'
import { OrderStatusBadge } from './OrderStatusBadge'
import { OrderInfoTab } from './OrderInfoTab'
import { OrderDeliveryTab } from './OrderDeliveryTab'
import { OrderCourierTab } from './OrderCourierTab'
import { OrderHistoryTimeline } from './OrderHistoryTimeline'

export interface OrderDrawerProps {
  orderId: number
  onClose: () => void
}

type OrderTab = 'info' | 'delivery' | 'courier' | 'history'

const TABS: Array<{ value: OrderTab; label: string }> = [
  { value: 'info', label: 'Информация' },
  { value: 'delivery', label: 'Доставка' },
  { value: 'courier', label: 'Курьер' },
  { value: 'history', label: 'История событий' },
]

const DETAILS_ERROR_FALLBACK = 'Не удалось загрузить карточку заказа'

// Технические сообщения axios не показываем пользователю.
const isTechnicalErrorMessage = (message: string): boolean =>
  /^(Request failed with status code|Network Error|timeout of\s)/i.test(
    message.trim()
  )

const getDetailsErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return DETAILS_ERROR_FALLBACK

  const apiError = error as Partial<ApiError> & {
    response?: { data?: { detail?: unknown } }
  }

  const responseDetail = apiError.response?.data?.detail
  if (typeof responseDetail === 'string' && responseDetail.trim()) {
    return responseDetail
  }

  const detail = apiError.detail
  if (
    typeof detail === 'string'
    && detail.trim()
    && !isTechnicalErrorMessage(detail)
  ) {
    return detail
  }

  return DETAILS_ERROR_FALLBACK
}

/**
 * Карточка заказа (read-only).
 * Кнопок сохранения и вызовов изменяющих эндпоинтов здесь нет.
 */
export const OrderDrawer: React.FC<OrderDrawerProps> = ({
  orderId,
  onClose,
}) => {
  const [tab, setTab] = useState<OrderTab>('info')

  const details = useOrderDetails(orderId)
  // История грузится только при открытии соответствующей вкладки.
  const history = useOrderHistory(orderId, { enabled: tab === 'history' })

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const order = details.data
  const sourceLabel = order?.sourceLabel || order?.source || '—'
  const legalEntityName = order?.legalEntityName || '—'
  const deliveryType = order?.deliveryTypeName || order?.deliveryType || '—'

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        className="absolute inset-0 bg-black bg-opacity-30"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-drawer-title"
        className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-xl"
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2
                id="order-drawer-title"
                className="truncate text-lg font-semibold text-gray-900"
              >
                Заказ {order?.number || `#${orderId}`}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                R-CRM ID: {order?.externalOrderId || '—'}
              </p>
              <p className="mt-0.5 break-words text-sm text-gray-500">
                Источник: {sourceLabel} · {legalEntityName}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {order ? (
                  <OrderStatusBadge
                    status={order.status}
                    statusText={order.statusText}
                  />
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
                <span className="text-xs text-gray-500">{deliveryType}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть карточку заказа"
              className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div
            role="tablist"
            aria-label="Разделы карточки заказа"
            className="mt-4 flex flex-wrap gap-2"
          >
            {TABS.map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                id={`order-tab-${item.value}`}
                aria-selected={tab === item.value}
                aria-controls="order-panel"
                onClick={() => setTab(item.value)}
                className={
                  tab === item.value
                    ? 'admin-button admin-button-primary'
                    : 'admin-button admin-button-secondary'
                }
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div
          role="tabpanel"
          id="order-panel"
          aria-labelledby={`order-tab-${tab}`}
          className="flex-1 overflow-y-auto p-6"
        >
          {details.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
              <span className="ml-3 text-sm text-gray-500">
                Загрузка карточки заказа...
              </span>
            </div>
          ) : details.isError ? (
            <div className="flex flex-col items-center py-8 text-center">
              <ExclamationTriangleIcon className="h-10 w-10 text-error-500" />
              <h3 className="mt-3 text-lg font-medium text-gray-900">
                Не удалось загрузить карточку заказа
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {getDetailsErrorMessage(details.error)}
              </p>
              <Button
                className="mt-4"
                variant="secondary"
                onClick={() => void details.refetch()}
              >
                Повторить
              </Button>
            </div>
          ) : !order ? (
            <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
              Данные заказа недоступны
            </div>
          ) : (
            <>
              {tab === 'info' && <OrderInfoTab order={order} />}
              {tab === 'delivery' && <OrderDeliveryTab order={order} />}
              {tab === 'courier' && <OrderCourierTab order={order} />}
              {tab === 'history' && (
                <OrderHistoryTimeline
                  items={history.data?.items}
                  isLoading={history.isLoading}
                  isError={history.isError}
                  onRetry={() => void history.refetch()}
                />
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  )
}
